import { useEffect, useState } from 'react';
import { Users, ArrowLeftRight, TrendingUp, ShieldCheck, AlertCircle, Clock, Bell } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import AdminLayout from '@/components/AdminLayout';
import { supabase } from '@/integrations/supabase/client';
import { formatCurrency, ROUTE_PATHS } from '@/lib/index';
import { useI18n } from '@/hooks/useI18n';
import { useAuth } from '@/hooks/useAuth';

interface Stats {
  totalClients: number;
  verifiedClients: number;
  pendingKyc: number;
  totalBalance: number;
}

interface RecentRegistration {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  kyc_status: string;
  plan: string;
  created_at: string;
}

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

export default function AdminDashboard() {
  const { t } = useI18n();
  const { profile } = useAuth();
  const [stats, setStats] = useState<Stats>({
    totalClients: 0,
    verifiedClients: 0,
    pendingKyc: 0,
    totalBalance: 0,
  });
  const [recentRegistrations, setRecentRegistrations] = useState<RecentRegistration[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [profilesRes, accountsRes, recentRes] = await Promise.all([
          supabase.from('profiles').select('kyc_status').eq('is_admin', false),
          supabase.from('bank_accounts').select('balance'),
          supabase
            .from('profiles')
            .select('id, email, first_name, last_name, kyc_status, plan, created_at')
            .eq('is_admin', false)
            .order('created_at', { ascending: false })
            .limit(10),
        ]);

        const profiles = profilesRes.data ?? [];
        const accounts = accountsRes.data ?? [];
        const recent = recentRes.data ?? [];

        setStats({
          totalClients: profiles.length,
          verifiedClients: profiles.filter((p) => p.kyc_status === 'verified').length,
          pendingKyc: profiles.filter((p) => ['pending', 'submitted'].includes(p.kyc_status)).length,
          totalBalance: accounts.reduce((sum, acc) => sum + (acc.balance ?? 0), 0),
        });

        setRecentRegistrations(recent);
      } catch (error) {
        console.error('Error loading admin dashboard:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const STAT_CARDS = [
    {
      labelKey: 'admin_total_clients',
      value: stats.totalClients,
      icon: Users,
      color: 'text-blue-400',
      bg: 'bg-blue-500/10',
      border: 'border-blue-500/20',
    },
    {
      labelKey: 'admin_verified',
      value: stats.verifiedClients,
      icon: ShieldCheck,
      color: 'text-green-400',
      bg: 'bg-green-500/10',
      border: 'border-green-500/20',
    },
    {
      labelKey: 'admin_pending_kyc',
      value: stats.pendingKyc,
      icon: Clock,
      color: 'text-yellow-400',
      bg: 'bg-yellow-500/10',
      border: 'border-yellow-500/20',
    },
    {
      labelKey: 'admin_total_balance',
      value: formatCurrency(stats.totalBalance),
      icon: TrendingUp,
      color: 'text-gold',
      bg: 'bg-gold/10',
      border: 'border-gold/20',
    },
  ];

  const QUICK_ACTIONS = [
    {
      to: ROUTE_PATHS.ADMIN_CLIENTS,
      labelKey: 'admin_clients_title',
      icon: Users,
      color: 'bg-blue-500/10 border-blue-500/20 text-blue-400',
    },
    {
      to: ROUTE_PATHS.ADMIN_TRANSACTIONS,
      labelKey: 'admin_transactions_title',
      icon: ArrowLeftRight,
      color: 'bg-purple-500/10 border-purple-500/20 text-purple-400',
    },
    {
      to: ROUTE_PATHS.ADMIN_NOTIFICATIONS,
      labelKey: 'notif_title',
      icon: Bell,
      color: 'bg-orange-500/10 border-orange-500/20 text-orange-400',
    },
  ];

  const getKycBadge = (status: string) => {
    const badges = {
      verified: { label: t('kyc_verified'), color: 'bg-green-500/10 text-green-400 border-green-500/20' },
      submitted: { label: t('kyc_submitted'), color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
      pending: { label: t('kyc_pending'), color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' },
      rejected: { label: t('kyc_rejected'), color: 'bg-red-500/10 text-red-400 border-red-500/20' },
    };
    return badges[status as keyof typeof badges] || badges.pending;
  };

  const getPlanBadge = (plan: string) => {
    const badges = {
      Standard: 'bg-border/50 text-foreground border-border',
      Plus: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      Premium: 'bg-gold/10 text-gold border-gold/20',
      Metal: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
    };
    return badges[plan as keyof typeof badges] || badges.Standard;
  };

  if (!profile?.is_admin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="border-red-500/20 bg-red-500/5 max-w-md">
          <CardContent className="p-8 text-center space-y-4">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto" />
            <div>
              <h2 className="text-xl font-bold text-red-300">{t('access_denied_title')}</h2>
              <p className="text-sm text-muted-foreground mt-2">{t('access_denied_desc')}</p>
            </div>
            <Link
              to={ROUTE_PATHS.HOME}
              className="inline-flex items-center justify-center px-6 py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity"
            >
              {t('access_denied_back')}
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <AdminLayout title={t('admin_dashboard_title')}>
      <motion.div variants={stagger} initial="hidden" animate="visible" className="space-y-8">
        <motion.div variants={fadeUp} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {STAT_CARDS.map((stat) => {
            const Icon = stat.icon;
            return (
              <Card key={stat.labelKey} className={`border ${stat.border} ${stat.bg}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{t(stat.labelKey)}</p>
                      <p className={`text-3xl font-bold ${stat.color}`}>{loading ? '...' : stat.value}</p>
                    </div>
                    <div
                      className={`w-12 h-12 rounded-xl ${stat.bg} ${stat.border} border flex items-center justify-center`}
                    >
                      <Icon className={`w-6 h-6 ${stat.color}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </motion.div>

        <motion.div variants={fadeUp}>
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="text-lg">{t('admin_quick_actions')}</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {QUICK_ACTIONS.map((action) => {
                const Icon = action.icon;
                return (
                  <Link
                    key={action.to}
                    to={action.to}
                    className={`flex flex-col items-center gap-3 p-6 rounded-xl border transition-all hover:scale-105 ${action.color}`}
                  >
                    <Icon className="w-8 h-8" />
                    <span className="text-sm font-medium text-center">{t(action.labelKey)}</span>
                  </Link>
                );
              })}
            </CardContent>
          </Card>
        </motion.div>

        <motion.div variants={fadeUp}>
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="text-lg">{t('admin_recent_registrations')}</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">{t('general_loading')}</div>
              ) : recentRegistrations.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">{t('general_no_data')}</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border/50">
                        <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground">
                          {t('admin_client_name')}
                        </th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground">
                          {t('admin_client_email')}
                        </th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground">
                          {t('admin_client_kyc')}
                        </th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground">
                          {t('admin_client_plan')}
                        </th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground">
                          {t('profile_created_at')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentRegistrations.map((reg) => {
                        const kycBadge = getKycBadge(reg.kyc_status);
                        const planBadge = getPlanBadge(reg.plan);
                        return (
                          <tr key={reg.id} className="border-b border-border/30 hover:bg-muted/30">
                            <td className="py-3 px-4 text-sm font-medium">
                              {reg.first_name} {reg.last_name}
                            </td>
                            <td className="py-3 px-4 text-sm text-muted-foreground">{reg.email}</td>
                            <td className="py-3 px-4">
                              <span
                                className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium border ${kycBadge.color}`}
                              >
                                {kycBadge.label}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span
                                className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium border ${planBadge}`}
                              >
                                {reg.plan}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-sm text-muted-foreground">
                              {new Date(reg.created_at).toLocaleDateString('pl-PL')}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    </AdminLayout>
  );
}